import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { CloseRounded } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { api } from '../../APIs/api';
import { API_URL, BASE_URL } from '../../APIs/endpoint';

const MONTH_COUNT = 10;
const MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];
const MONTH_FULL = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

const getUpcomingMonths = (count = MONTH_COUNT) => {
  const now = new Date();
  const months = [];

  for (let i = 0; i < count; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const monthIndex = date.getMonth();
    const year = date.getFullYear();
    const shortYear = String(year).slice(-2);

    months.push({
      label: `${MONTH_ABBR[monthIndex]} '${shortYear}`,
      value: `${MONTH_FULL[monthIndex]} ${year}`
    });
  }

  return months;
};

const initialFormValues = {
  name: '',
  email: '',
  phoneNumber: '',
  travelMonth: ''
};

const initialErrors = {
  name: '',
  email: '',
  phoneNumber: '',
  travelMonth: ''
};

const PlanYourJourneyModal = ({ open, onClose, tourPackage }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { slug } = useParams();
  const nameInputRef = useRef(null);
  const [formValues, setFormValues] = useState(initialFormValues);
  const [errors, setErrors] = useState(initialErrors);
  const [isLoader, setIsLoader] = useState(false);
  const travelMonths = useMemo(() => getUpcomingMonths(MONTH_COUNT), []);

  useEffect(() => {
    if (open && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [open]);

  const validateInput = (field, value) => {
    let error = '';
    switch (field) {
      case 'name':
        if (!value.trim()) {
          error = 'Name is required';
        } else if (value.length > 50) {
          error = 'Name cannot exceed 50 characters';
        } else if (!/^[a-zA-Z\s]*$/.test(value)) {
          error = 'Name only accepts letters and spaces';
        }
        break;
      case 'email': {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!value.trim()) {
          error = 'Email is required';
        } else if (value.length > 50) {
          error = 'Email cannot exceed 50 characters';
        } else if (!emailRegex.test(value)) {
          error = 'Please enter a valid email address';
        }
        break;
      }
      case 'phoneNumber':
        if (!value.trim()) {
          error = 'Phone number is required';
        } else if (!/^(\+?\d{1,15})$/.test(value)) {
          error = 'Phone number must contain only numbers and optional +';
        } else if (value.replace('+', '').length < 10 || value.replace('+', '').length > 15) {
          error = 'Phone number must be between 10 and 15 digits';
        }
        break;
      case 'travelMonth':
        if (!value) {
          error = 'Please select a travel month';
        }
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [field]: error }));
    return error;
  };

  const handleInputChange = (e) => {
    const { id, name, value } = e.target;
    const field = id || name;
    setFormValues((prev) => ({ ...prev, [field]: value }));
    validateInput(field, value);
  };

  const handleMonthSelect = (monthValue) => {
    setFormValues((prev) => ({ ...prev, travelMonth: monthValue }));
    validateInput('travelMonth', monthValue);
  };

  const isFormValid = () =>
    Object.values(formValues).every((field) => String(field).trim()) &&
    !Object.values(errors).some((error) => error);

  const handleCloseWithReset = () => {
    setFormValues(initialFormValues);
    setErrors(initialErrors);
    setIsLoader(false);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const nextErrors = {
      name: validateInput('name', formValues.name),
      email: validateInput('email', formValues.email),
      phoneNumber: validateInput('phoneNumber', formValues.phoneNumber),
      travelMonth: validateInput('travelMonth', formValues.travelMonth)
    };

    if (Object.values(nextErrors).some((error) => error)) return;

    setIsLoader(true);
    try {
      const packageId = tourPackage?.new_item_code || tourPackage?.id || 'N/A';
      const destination =
        tourPackage?.country === 'India'
          ? tourPackage?.state || tourPackage?.package_name || 'N/A'
          : tourPackage?.country || tourPackage?.package_name || 'N/A';

      const enquiryData = {
        name: formValues.name.trim(),
        email: formValues.email.trim(),
        mobile: formValues.phoneNumber.trim(),
        month: formValues.travelMonth,
        package_id: packageId,
        destination
      };

      const enquiryRes = await api.post(`${BASE_URL}${API_URL.CREATE_ENQUIRY}`, enquiryData);
      const { status, data, message } = enquiryRes;

      if (status) {
        toast.success(typeof data === 'string' ? data : message || 'Submitted successfully', {
          autoClose: 3000
        });
        handleCloseWithReset();

        if (slug && packageId !== 'N/A') {
          sessionStorage.setItem(
            'tripDetails',
            JSON.stringify({
              countryName: destination,
              packageId
            })
          );
          navigate(`/destination/${slug}/${packageId}/trip-confirmation`);
        }
      } else {
        toast.error(typeof data === 'string' ? data : message || 'Something went wrong', {
          autoClose: 3000
        });
      }
    } catch (error) {
      toast.error(error.message || 'Something went wrong', { autoClose: 3000 });
    } finally {
      setIsLoader(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      disableEscapeKeyDown
      maxWidth="xs"
      fullWidth
      fullScreen={isMobile}
      aria-labelledby="plan-your-journey-dialog-title"
      sx={{ zIndex: 10000 }}
      slotProps={{
        backdrop: {
          sx: { zIndex: 9999 }
        }
      }}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : 'auto',
          maxWidth: isMobile ? '100%' : '520px',
          margin: isMobile ? '0' : 'auto',
          marginTop: isMobile ? '20%' : '5%',
          height: isMobile ? 'auto' : 'auto',
          maxHeight: isMobile ? 'calc(100% - 10%)' : '90vh',
          borderRadius: isMobile ? '12px 12px 0 0' : '8px',
          zIndex: 10001
        }
      }}>
      <form onSubmit={handleSubmit} autoComplete="off">
        <DialogTitle id="plan-your-journey-dialog-title" sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography
              sx={{
                fontFamily: 'gothamBold',
                fontSize: { xs: '18px', sm: '20px' },
                color: '#27306B'
              }}>
              Get a free quote
            </Typography>
            <IconButton
              aria-label="close"
              onClick={handleCloseWithReset}
              disabled={isLoader}
              sx={{ color: (t) => t.palette.grey[500] }}>
              <CloseRounded />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: '8px !important' }}>
          <TextField
            autoFocus
            inputRef={nameInputRef}
            margin="dense"
            id="name"
            label="Name"
            type="text"
            fullWidth
            variant="outlined"
            value={formValues.name}
            onChange={handleInputChange}
            required
            inputProps={{ maxLength: 50 }}
            helperText={errors.name}
            error={!!errors.name}
            disabled={isLoader}
          />
          <TextField
            margin="dense"
            id="email"
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={formValues.email}
            onChange={handleInputChange}
            required
            inputProps={{ maxLength: 50 }}
            helperText={errors.email}
            error={!!errors.email}
            disabled={isLoader}
          />
          <TextField
            margin="dense"
            id="phoneNumber"
            label="Phone Number"
            type="tel"
            fullWidth
            variant="outlined"
            value={formValues.phoneNumber}
            onChange={handleInputChange}
            required
            inputProps={{ maxLength: 15 }}
            helperText={errors.phoneNumber}
            error={!!errors.phoneNumber}
            disabled={isLoader}
          />

          <Box sx={{ mt: 2, mb: 0.5 }}>
            <Typography
              sx={{
                fontFamily: 'gothamBook',
                fontSize: '14px',
                color: errors.travelMonth ? '#d32f2f' : '#666',
                mb: 1.2
              }}>
              Planned travel month *
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1
              }}>
              {travelMonths.map((month) => {
                const isSelected = formValues.travelMonth === month.value;
                return (
                  <Button
                    key={month.value}
                    type="button"
                    disableElevation
                    disabled={isLoader}
                    onClick={() => handleMonthSelect(month.value)}
                    sx={{
                      minWidth: { xs: '68px', sm: '76px' },
                      px: 1.5,
                      py: 1,
                      borderRadius: '6px',
                      textTransform: 'none',
                      fontFamily: isSelected ? 'gothamBold' : 'gothamBook',
                      fontSize: '13px',
                      border: '1px solid #1a1a1a',
                      backgroundColor: isSelected ? '#2b2b2b' : '#fff',
                      color: isSelected ? '#fff' : '#1a1a1a',
                      '&:hover': {
                        backgroundColor: isSelected ? '#1f1f1f' : '#f5f5f5',
                        border: '1px solid #1a1a1a'
                      },
                      '&.Mui-disabled': {
                        border: '1px solid #ccc',
                        color: '#999',
                        backgroundColor: '#f0f0f0'
                      }
                    }}>
                    {month.label}
                  </Button>
                );
              })}
            </Box>
            {errors.travelMonth && (
              <Typography sx={{ color: '#d32f2f', fontSize: '12px', mt: 0.8 }}>
                {errors.travelMonth}
              </Typography>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'center', px: 3, pb: 3 }}>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={!isFormValid() || isLoader}
            sx={{
              backgroundColor: '#E41E25',
              color: '#fff',
              fontFamily: 'gothamBold',
              textTransform: 'none',
              py: 1.2,
              '&:hover': { backgroundColor: '#c4191f' },
              '&:disabled': {
                backgroundColor: '#E41E25',
                color: '#fff',
                opacity: 0.5
              }
            }}>
            {isLoader ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Submit'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PlanYourJourneyModal;
